import { useCallback, useState } from 'react'

export interface SelectionManager {
  selectedIds: Set<string>
  toggleEntry: (entryId: string) => void
  toggleGroup: (entryIds: string[]) => void
  selectAll: (entryIds: string[]) => void
  deselectAll: (entryIds: string[]) => void
  isSelected: (entryId: string) => boolean
  isGroupFullySelected: (entryIds: string[]) => boolean
  isGroupPartiallySelected: (entryIds: string[]) => boolean
}

export const useSelectionManager = (
  initialSelection: Set<string> = new Set()
): SelectionManager => {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(initialSelection)

  const toggleEntry = useCallback((entryId: string) => {
    setSelectedIds((prev) => {
      const newSelected = new Set(prev)
      if (newSelected.has(entryId)) {
        newSelected.delete(entryId)
      } else {
        newSelected.add(entryId)
      }
      return newSelected
    })
  }, [])

  const toggleGroup = useCallback((entryIds: string[]) => {
    setSelectedIds((prev) => {
      const newSelected = new Set(prev)
      const allSelected = entryIds.every((id) => newSelected.has(id))

      if (allSelected) {
        // Deselect all entries in the group
        entryIds.forEach((id) => newSelected.delete(id))
      } else {
        // Select all entries in the group
        entryIds.forEach((id) => newSelected.add(id))
      }

      return newSelected
    })
  }, [])

  const selectAll = useCallback((entryIds: string[]) => {
    setSelectedIds((prev) => {
      const newSelected = new Set(prev)
      entryIds.forEach((id) => newSelected.add(id))
      return newSelected
    })
  }, [])

  const deselectAll = useCallback((entryIds: string[]) => {
    setSelectedIds((prev) => {
      const newSelected = new Set(prev)
      entryIds.forEach((id) => newSelected.delete(id))
      return newSelected
    })
  }, [])

  const isSelected = useCallback(
    (entryId: string) => {
      return selectedIds.has(entryId)
    },
    [selectedIds]
  )

  const isGroupFullySelected = useCallback(
    (entryIds: string[]) => {
      return entryIds.length > 0 && entryIds.every((id) => selectedIds.has(id))
    },
    [selectedIds]
  )

  const isGroupPartiallySelected = useCallback(
    (entryIds: string[]) => {
      const selectedCount = entryIds.filter((id) => selectedIds.has(id)).length
      return selectedCount > 0 && selectedCount < entryIds.length
    },
    [selectedIds]
  )

  return {
    selectedIds,
    toggleEntry,
    toggleGroup,
    selectAll,
    deselectAll,
    isSelected,
    isGroupFullySelected,
    isGroupPartiallySelected,
  }
}
